// src/utils/pdfUtils.js
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME || "PHARMACIE NDIOL FOUTA";
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME || "DR. AL Ousseynou KEITA";
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1 || "Sor Diagne Route Nationale N 2";
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2 || "X Médina Bois Saint-Louis - Tél: +221 33 961 08 80";
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO || "NINEA:007968079 - RC: SN-STL-2020-A-1225";

const formatPrice = (price) => {
    if (!price && price !== 0) return '0 Fcfa';
    
    // Convertir en nombre
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Formater manuellement avec des espaces
    const formattedPrice = numericPrice.toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    return `${formattedPrice} Fcfa`;
};

// Fonction pour grouper les commandes par référence
const groupCommandesByReference = (commandes) => {
  const groups = {};
  
  commandes.forEach(commande => {
    const reference = commande.numero || 'N/A';
    
    if (!groups[reference]) {
      groups[reference] = {
        reference: reference,
        fournisseur: commande.nomFournisseur,
        createur: commande.speudo,
        produits: []
      };
    }
    
    groups[reference].produits.push(commande);
  });
  
  return Object.values(groups);
};

// Fonction pour créer l'en-tête du PDF
const createPDFHeader = (doc, title) => {
  // Couleurs
  const primaryColor = [34, 109, 104]; // #226D68
  const secondaryColor = [242, 101, 34]; // #f26522
  const darkGray = [108, 117, 125];

  // En-tête professionnel
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');

  // Dessiner une icône pharmacie simple avec des formes
  doc.setFillColor(255, 255, 255);
  
  // Croix médicale simple
  doc.rect(17, 12, 10, 2, 'F'); // Barre horizontale
  doc.rect(21, 8, 2, 10, 'F');  // Barre verticale
  
  // Cercle autour (optionnel)
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.5);
  doc.circle(22, 13, 8, 'D');
    
  // Nom de la pharmacie
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text(PHARMACY_NAME, 35, 20);
  
  // Sous-titre
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 15, 30);

  // Informations de l'établissement
  doc.setFontSize(8);
  doc.setTextColor(...darkGray);
  doc.text(OWNER_NAME, 15, 50);
  doc.text(ADDRESS_LINE1, 15, 55);
  doc.text(ADDRESS_LINE2, 15, 60);
  doc.text(BUSINESS_INFO, 15, 65);

  // Titre du rapport
  doc.setFontSize(16);
  doc.setTextColor(...primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), 105, 50, { align: 'center' });

  // Date de génération
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);
  doc.text(`Généré le: ${dateStr}`, 105, 60, { align: 'left' });

  return { doc, now, primaryColor, secondaryColor, darkGray };
};

// Fonction pour créer le tableau avec regroupement par référence (SIMPLIFIÉE)
const createGroupedCommandesTable = (doc, commandes, startY, primaryColor, darkGray, lightGray) => {
  // Grouper les commandes par référence
  const groupedCommandes = groupCommandesByReference(commandes);

  // En-têtes du tableau simplifiées
  const tableHeaders = [
    'PRODUIT', 
    'PRIX UNITAIRE', 
    'QUANTITÉ',
    'QUANTITÉ LIVRÉE',
    'STATUT'
  ];

  // Préparer toutes les lignes du tableau
  const allTableRows = [];

  groupedCommandes.forEach((group, groupIndex) => {
    // Ajouter les produits de ce groupe
    group.produits.forEach((produit, prodIndex) => {
      const quantite = parseInt(produit.qteComder) || 0;
      const quantiteLivree = parseInt(produit.quan_livree) || 0;
      const prixUnitaire = parseFloat(produit.prixUnitaire) || 0;

      const produitRow = [
        produit.nomProd,
        formatPrice(prixUnitaire),
        quantite,
        quantiteLivree,
        quantiteLivree > 0 ? 'Livrée' : 'En attente'
      ];

      allTableRows.push(produitRow);
    });

    // Ajouter une ligne vide entre les groupes (sauf pour le dernier groupe)
    if (groupIndex < groupedCommandes.length - 1) {
      allTableRows.push(['', '', '', '', '']);
    }
  });

  // Générer le tableau avec autoTable
  doc.autoTable({
    startY: startY,
    head: [tableHeaders],
    body: allTableRows,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: primaryColor,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: primaryColor,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8,
    },
    bodyStyles: {
      textColor: darkGray,
    },
    alternateRowStyles: {
      fillColor: lightGray,
    },
    columnStyles: {
      0: { cellWidth: 60 }, // PRODUIT
      1: { cellWidth: 30, halign: 'right' }, // PRIX UNITAIRE
      2: { cellWidth: 25, halign: 'center' }, // QUANTITÉ
      3: { cellWidth: 30, halign: 'center' }, // QUANTITÉ LIVRÉE
      4: { cellWidth: 25, halign: 'center' }, // STATUT
    },
    margin: { left: 15, right: 15 },
  });

  return doc.lastAutoTable.finalY;
};

// Fonction pour ajouter les informations de référence au-dessus du tableau
const addReferenceInfo = (doc, groupedCommandes, startY, primaryColor, darkGray) => {
  let currentY = startY;
  
  groupedCommandes.forEach((group, index) => {
    // Vérifier si on a besoin d'une nouvelle page
    if (currentY > 250) {
      doc.addPage();
      currentY = 20;
    }
    
    // Informations de référence, fournisseur et créateur
    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.setFont('helvetica', 'bold');
    
    // Cadre d'informations
    doc.setFillColor(240, 248, 255);
    doc.rect(15, currentY, 180, 20, 'F');
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.rect(15, currentY, 180, 20, 'D');
    
    // Texte des informations
    doc.text(`Référence: ${group.reference}`, 20, currentY + 8);
    doc.text(`Fournisseur: ${group.fournisseur || 'N/A'}`, 20, currentY + 15);
    doc.text(`Créateur: ${group.createur || 'N/A'}`, 110, currentY + 8);
    
    currentY += 25;
    
    // Ajouter le tableau pour ce groupe
    const tableHeaders = ['PRODUIT', 'PRIX UNITAIRE', 'QUANTITÉ', 'QUANTITÉ LIVRÉE', 'STATUT'];
    const tableRows = [];
    
    group.produits.forEach(produit => {
      const quantite = parseInt(produit.qteComder) || 0;
      const quantiteLivree = parseInt(produit.quan_livree) || 0;
      const prixUnitaire = parseFloat(produit.prixUnitaire) || 0;

      tableRows.push([
        produit.nomProd,
        formatPrice(prixUnitaire),
        quantite,
        quantiteLivree,
        quantiteLivree > 0 ? 'Livrée' : 'En attente'
      ]);
    });
    
    doc.autoTable({
      startY: currentY,
      head: [tableHeaders],
      body: tableRows,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: primaryColor,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
      },
      bodyStyles: {
        textColor: darkGray,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 30, halign: 'right' },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
      },
      margin: { left: 15, right: 15 },
    });
    
    currentY = doc.lastAutoTable.finalY + 15;
    
    // Ajouter un espace entre les groupes (sauf pour le dernier)
    if (index < groupedCommandes.length - 1) {
      currentY += 10;
    }
  });
  
  return currentY;
};

// Fonction pour ajouter le total à la fin
const addTotalSection = (doc, commandes, finalY) => {
  const primaryColor = [34, 109, 104];
  const secondaryColor = [242, 101, 34];
  
  // Calculer les nouveaux totaux
  const totalCommandes = commandes.length;
  const totalReferences = new Set(commandes.map(cmd => cmd.numero)).size; // Nombre de références uniques
  const totalQuantite = commandes.reduce((sum, cmd) => sum + (parseInt(cmd.qteComder) || 0), 0);
  const totalQuantiteLivree = commandes.reduce((sum, cmd) => sum + (parseInt(cmd.quan_livree) || 0), 0);
  
  // Calcul de la valeur totale (quantité livrée × prix unitaire)
  const totalValeur = commandes.reduce((sum, cmd) => {
    const prix = parseFloat(cmd.prixUnitaire) || 0;
    const qteLivree = parseInt(cmd.quan_livree) || 0;
    return sum + (prix * qteLivree);
  }, 0);

  // Position pour la section total
  const totalY = finalY + 15;

  // Fond coloré pour la section total
  doc.setFillColor(...primaryColor);
  doc.rect(15, totalY, 180, 28, 'F');

  // Texte des totaux
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  
  // Colonne gauche
  doc.text(`Nombre de commandes: ${totalCommandes}`, 20, totalY + 7);
  doc.text(`Nombre de références: ${totalReferences}`, 20, totalY + 14);
  doc.text(`Quantité commandée: ${totalQuantite}`, 20, totalY + 21);
  
  // Colonne droite
  doc.text(`Quantité livrée: ${totalQuantiteLivree}`, 110, totalY + 7);
  doc.text(`Valeur livrée: ${formatPrice(totalValeur)}`, 110, totalY + 14);

  return totalY + 35;
};

// Fonction pour créer le pied de page
const createPDFFooter = (doc, pharmacyName) => {
  const darkGray = [108, 117, 125];
  const pageCount = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...darkGray);
    doc.text(
      `Page ${i} sur ${pageCount} - ${pharmacyName}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
};

// Fonction principale pour générer le PDF des commandes (MODIFIÉE)
export const generateCommandesPDF = (commandes) => {
  const doc = new jsPDF();
  const lightGray = [248, 249, 250];

  // Créer l'en-tête avec le nouveau titre
  const { now, primaryColor, darkGray } = createPDFHeader(doc, 'COMMANDES FOURNISSEURS');

  // Grouper les commandes par référence
  const groupedCommandes = groupCommandesByReference(commandes);

  // Ajouter les informations de référence et les tableaux groupés
  const startY = 70;
  const finalY = addReferenceInfo(doc, groupedCommandes, startY, primaryColor, darkGray);

  // Ajouter la section total après le tableau
  addTotalSection(doc, commandes, finalY);

  // Pied de page
  createPDFFooter(doc, PHARMACY_NAME);

  // Sauvegarde du PDF
  const fileName = `commandes_fournisseurs_${now.toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);

  return fileName;
};

// Fonction générique pour créer des PDF avec des données personnalisées
export const generateCustomPDF = (data, options = {}) => {
  const {
    title = 'RAPPORT',
    headers = [],
    bodyData = [],
    fileName = `rapport_${new Date().toISOString().split('T')[0]}.pdf`,
    columnStyles = {},
    showTotal = false
  } = options;

  const doc = new jsPDF();
  const lightGray = [248, 249, 250];

  // Créer l'en-tête
  const { now, primaryColor } = createPDFHeader(doc, title);

  // Génération du tableau
  const tableResult = doc.autoTable({
    startY: 70,
    head: [headers],
    body: bodyData,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: primaryColor,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: primaryColor,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 8,
    },
    alternateRowStyles: {
      fillColor: lightGray,
    },
    columnStyles: columnStyles,
    margin: { left: 15, right: 15 },
  });

  // Ajouter la section total si demandé
  if (showTotal && data && data.length > 0) {
    const finalY = tableResult.lastAutoTable.finalY;
    
    // Calculer le total simple (nombre d'éléments)
    const totalItems = data.length;
    
    const totalY = finalY + 15;
    doc.setFillColor(...primaryColor);
    doc.rect(15, totalY, 180, 12, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${totalItems} éléments`, 20, totalY + 8);
  }

  // Pied de page
  createPDFFooter(doc, PHARMACY_NAME);

  // Sauvegarde du PDF
  doc.save(fileName);

  return fileName;
};

// Export des constantes pour une utilisation dans d'autres composants
export const PDF_CONSTANTS = {
  PHARMACY_NAME,
  OWNER_NAME,
  ADDRESS_LINE1,
  ADDRESS_LINE2,
  BUSINESS_INFO
};

export { formatPrice };